(() => {
  const sheet = document.getElementById("product-sheet");
  const body = document.body;
  // Toggle/drag target: handle inside the sheet header
  const header = sheet?.querySelector?.("[data-toggle]");
  const backdrop = document.querySelector("[data-backdrop]");
  const bodyScrollEl = sheet?.querySelector?.(".brn-product__sheet__content");
  const status = document.getElementById("status");
  // Keep these nodes interactive even while the sheet is modalized
  const KEEP_ACTIVE_SELECTOR = "[data-sheet-keep-active], .wt-product__sticky-buy";
  let lastDragWasMove = false;
  let lastFocused = null;

  // Elements outside the sheet that must be silenced when the dialog is open
  const backgroundElements = new Set();
  const previousAriaHidden = new Map();
  const previousInertState = new Map();

  function collectBackgroundElements() {
    backgroundElements.clear();
    if (!sheet) return;
    const exclusions = new Set([sheet, backdrop]);
    if (status) exclusions.add(status);
    let current = sheet;
    while (current && current !== document.body) {
      const parent = current.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach(child => {
        if (child === current) return;
        if (exclusions.has(child)) return;
        if (child.matches?.(KEEP_ACTIVE_SELECTOR)) {
          exclusions.add(child);
          return;
        }
        backgroundElements.add(child);
      });
      exclusions.add(parent);
      current = parent;
    }
  }

  function setBackgroundDisabled(disabled) {
    const elements = Array.from(backgroundElements);
    elements.forEach(el => {
      if (el.matches?.(KEEP_ACTIVE_SELECTOR)) return;
      if (disabled) {
        if (!previousAriaHidden.has(el)) previousAriaHidden.set(el, el.getAttribute("aria-hidden"));
        if (!previousInertState.has(el)) previousInertState.set(el, el.hasAttribute("inert"));
        el.setAttribute("aria-hidden", "true");
        el.setAttribute("inert", "");
      } else {
        const prevHidden = previousAriaHidden.get(el);
        if (prevHidden === null) el.removeAttribute("aria-hidden");
        else el.setAttribute("aria-hidden", prevHidden);
        previousAriaHidden.delete(el);

        const hadInert = previousInertState.get(el);
        if (hadInert) el.setAttribute("inert", "");
        else el.removeAttribute("inert");
        previousInertState.delete(el);
      }
    });
    if (!disabled) {
      backgroundElements.clear();
      previousAriaHidden.clear();
      previousInertState.clear();
    }
  }

  function onTransitionEndOnce(cb) {
    const handler = ev => {
      if (ev.propertyName !== "height") return;
      sheet.removeEventListener("transitionend", handler);
      sheet.classList.remove("sheet--animating");
      cb?.();
    };
    sheet.addEventListener("transitionend", handler);
    // Fallback in case the event doesn't fire
    setTimeout(() => {
      sheet.removeEventListener("transitionend", handler);
      sheet.classList.remove("sheet--animating");
      cb?.();
    }, 400);
  }

  function getFocusable(container = sheet) {
    return Array.from(container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(
      el => !el.hasAttribute("hidden") && getComputedStyle(el).display !== "none"
    );
  }

  function trapTab(e) {
    if (e.key !== "Tab") return;
    const f = getFocusable();
    if (f.length === 0) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  function enableFocusTrap() {
    sheet.addEventListener("keydown", trapTab);
  }
  function disableFocusTrap() {
    sheet.removeEventListener("keydown", trapTab);
  }

  function openSheet() {
    if (!sheet) return;
    lastFocused = document.activeElement;
    sheet.classList.add("sheet--animating");
    sheet.dataset.state = "open";
    sheet.setAttribute("aria-expanded", "true");
    sheet.setAttribute("aria-modal", "true");

    // Enhanced scroll lock for iOS Safari
    body.classList.add("no-scroll");

    backdrop.hidden = false;
    backdrop.setAttribute("data-visible", "true");
    collectBackgroundElements();
    setBackgroundDisabled(true);
    enableFocusTrap();
    onTransitionEndOnce(() => {
      // Move focus to the scrollable content when animation ends
      bodyScrollEl?.focus();
    });
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.classList.add("sheet--animating");
    sheet.dataset.state = "closed";
    sheet.setAttribute("aria-expanded", "false");
    sheet.setAttribute("aria-modal", "false");
    // Reset inner scroll so reopening starts at the top
    if (bodyScrollEl) bodyScrollEl.scrollTop = 0;

    // Remove enhanced scroll lock for iOS Safari
    body.classList.remove("no-scroll");

    backdrop.removeAttribute("data-visible");
    setBackgroundDisabled(false);
    disableFocusTrap();
    onTransitionEndOnce();
    // Wait for backdrop fade before hiding it
    setTimeout(() => {
      if (!backdrop.getAttribute("data-visible")) backdrop.hidden = true;
    }, 240);
    // Restore focus to the trigger
    setTimeout(() => {
      if (lastFocused && document.contains(lastFocused)) {
        try {
          lastFocused.focus();
        } catch { }
      } else {
        try {
          header?.focus();
        } catch { }
      }
    }, 10);
  }

  function toggle() {
    if (sheet.dataset.state === "open") closeSheet();
    else openSheet();
  }

  // If key elements are missing, abort initialization gracefully
  if (!sheet || !header || !bodyScrollEl || !backdrop) {
    console.warn("[sheet] Initialization skipped: missing required elements");
    return;
  }

  header.addEventListener("click", e => {
    if (lastDragWasMove) {
      // Prevent drag from triggering a click
      e.preventDefault();
      e.stopPropagation();
      lastDragWasMove = false;
      return;
    }
    toggle();
  });
  // Keyboard activation on header
  header.addEventListener("keydown", e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  });
  backdrop.addEventListener("click", closeSheet);

  // Accessible size radio group
  const sizesGroup = sheet.querySelector(".sizes");
  const sizeOptions = Array.from(sheet.querySelectorAll('.sizes [role="radio"]'));
  function selectSize(idx) {
    sizeOptions.forEach((btn, i) => btn.setAttribute("aria-checked", String(i === idx)));
    sizeOptions[idx]?.focus?.();
  }
  if (sizesGroup && sizeOptions.length) {
    sizeOptions.forEach((btn, i) => {
      btn.addEventListener("click", () => selectSize(i));
      btn.addEventListener("keydown", e => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          selectSize((i + 1) % sizeOptions.length);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          selectSize((i - 1 + sizeOptions.length) % sizeOptions.length);
        }
      });
    });
  }

  // Close on Escape
  window.addEventListener("keydown", e => {
    if (e.key === "Escape" && sheet.dataset.state === "open") closeSheet();
  });

  // Vertical drag on header (handle)
  let dragging = false;
  let startY = 0;
  // let startState = "closed";
  let startHeight = 0;
  let collapsedH = 128; // fallback, overwritten by the CSS var value
  let maxOpenH = Math.round((window.visualViewport?.height || window.innerHeight) * 0.9);
  let rafId = null; // throttle pointermove
  let lastY = 0;

  // Calculate and set sheet collapsed height based on header content
  function setSheetCollapsedHeight() {
    const header = document.querySelector('.brn-product__header');
    if (!header) return;

    // Measure dynamic elements
    const stickyBtn = document.querySelector('.wt-product__sticky-buy');
    const handle = document.querySelector('.brn-product__sheet__handle');

    const stickyBtnHeight = stickyBtn ? stickyBtn.offsetHeight : 0;
    const handleHeight = handle ? handle.offsetHeight : 0;

    // Get actual header height including all content (coming soon, title, price)
    const headerHeight = header.getBoundingClientRect().height;

    // Calculate total collapsed height: Button + Handle + Header
    const collapsedHeight = Math.ceil(stickyBtnHeight + handleHeight + headerHeight);

    // Update CSS variable
    document.documentElement.style.setProperty('--sheet-collapsed-h', `${collapsedHeight}px`);

    console.log(`[sheet] Calculation: Button(${stickyBtnHeight}) + Handle(${handleHeight}) + Header(${Math.ceil(headerHeight)}) = ${collapsedHeight}px`);
  }

  function readVars() {
    const root = getComputedStyle(document.documentElement);
    const v = root.getPropertyValue("--sheet-collapsed-h");
    const parsed = parseInt(v, 10);
    if (!Number.isNaN(parsed)) collapsedH = parsed;
    maxOpenH = Math.round((window.visualViewport?.height || window.innerHeight) * 0.9);
  }

  let captureEl = header; // elemento que captura el puntero (header o sheet)

  // Velocidad del gesto (px/ms, positivo = hacia abajo)
  let dragVelocity = 0;
  let prevDragY = 0;
  let prevDragTime = 0;

  function onPointerDown(e) {
    readVars();
    dragging = true;
    lastDragWasMove = false;
    startY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    startHeight = sheet.getBoundingClientRect().height;
    sheet.classList.add("sheet--dragging");
    captureEl = e.currentTarget ?? header;
    dragVelocity = 0;
    prevDragY = startY;
    prevDragTime = Date.now();
    try {
      captureEl.setPointerCapture?.(e.pointerId);
    } catch { }
    body.style.userSelect = "none";
  }

  function onPointerMove(e) {
    if (!dragging) return;
    lastY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const delta = lastY - startY; // + down, - up
      if (Math.abs(delta) > 4) lastDragWasMove = true;
      const target = Math.max(collapsedH, Math.min(maxOpenH, startHeight - delta));
      sheet.style.height = `${target}px`;

      // Velocidad instantánea del gesto (px/ms)
      const now = Date.now();
      const dt = now - prevDragTime;
      if (dt > 0) dragVelocity = (lastY - prevDragY) / dt;
      prevDragY = lastY;
      prevDragTime = now;
    });
  }

  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    body.style.userSelect = "";
    sheet.classList.remove("sheet--dragging");
    if (e?.pointerId !== null) {
      try {
        captureEl.releasePointerCapture?.(e.pointerId);
      } catch { }
    }
    // cleanup any running rAF
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function onPointerUp(e) {
    if (!dragging) return;

    // Decide final state: velocidad del gesto tiene prioridad, luego posición
    const currentH = parseInt(sheet.style.height || `${startHeight}`, 10);
    const midpoint = collapsedH + (maxOpenH - collapsedH) * 0.5;
    const FLICK_DOWN =  0.4; // px/ms → flick rápido abajo = cerrar
    const FLICK_UP   = -0.4; // px/ms → flick rápido arriba = abrir

    let open;
    if (dragVelocity > FLICK_DOWN)        open = false;
    else if (dragVelocity < FLICK_UP)     open = true;
    else                                   open = currentH >= midpoint;

    if (open) openSheet();
    else closeSheet();

    // Visual reset
    sheet.style.height = "";
    endDrag(e);
  }

  // Pointer Events (with touch fallback)
  header.addEventListener("pointerdown", onPointerDown);
  header.addEventListener("pointermove", onPointerMove);
  header.addEventListener("pointerup", onPointerUp);
  header.addEventListener("pointercancel", onPointerUp);
  header.addEventListener("lostpointercapture", endDrag);
  window.addEventListener("blur", endDrag);

  // Fallback for older browsers: Touch events
  header.addEventListener("touchstart", onPointerDown, { passive: true });
  header.addEventListener("touchmove", onPointerMove, { passive: true });
  header.addEventListener("touchend", onPointerUp, { passive: true });

  // ── Drag-to-open desde la zona colapsada (título, precio) ────────────────
  // Permite arrastrar hacia arriba desde cualquier parte visible de la sheet
  // cerrada, preservando la interactividad de botones y enlaces.
  function isInteractive(el) {
    return !!el.closest('button, a, input, label, select, [role="button"]');
  }

  sheet.addEventListener("pointerdown", (e) => {
    if (sheet.dataset.state !== "closed") return;
    if (isInteractive(e.target)) return;
    if (e.target.closest("[data-toggle]")) return; // ya lo gestiona header
    onPointerDown(e);
  });
  sheet.addEventListener("pointermove", (e) => { if (dragging) onPointerMove(e); });
  sheet.addEventListener("pointerup",   (e) => { if (dragging) onPointerUp(e); });
  sheet.addEventListener("pointercancel", (e) => { if (dragging) onPointerUp(e); });

  // Drag-to-close on sheet content (only when scrolled to top and dragging down)
  if (bodyScrollEl) {
    let contentStartY = 0;
    let contentDragActive = false;

    bodyScrollEl.addEventListener("touchstart", (e) => {
      contentStartY = e.touches[0].clientY;
      contentDragActive = false;
    }, { passive: true });

    bodyScrollEl.addEventListener("touchmove", (e) => {
      const dy = e.touches[0].clientY - contentStartY;
      if (!contentDragActive) {
        if (bodyScrollEl.scrollTop <= 0 && dy > 8) {
          contentDragActive = true;
          readVars();
          dragging = true;
          lastDragWasMove = false;
          startY = contentStartY;
          startHeight = sheet.getBoundingClientRect().height;
          sheet.classList.add("sheet--dragging");
          body.style.userSelect = "none";
        } else {
          return;
        }
      }
      e.preventDefault();
      lastY = e.touches[0].clientY;
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const delta = lastY - startY;
        if (Math.abs(delta) > 4) lastDragWasMove = true;
        const target = Math.max(collapsedH, Math.min(maxOpenH, startHeight - delta));
        sheet.style.height = `${target}px`;
      });
    }, { passive: false });

    bodyScrollEl.addEventListener("touchend", (e) => {
      if (!contentDragActive) return;
      contentDragActive = false;
      onPointerUp(e);
    }, { passive: true });
  }

  // Recalculate limits on viewport changes (virtual keyboard, rotation, etc.)
  const recalc = () => {
    setSheetCollapsedHeight(); // Recalculate header height first
    readVars();
  };
  window.addEventListener("resize", recalc);
  window.visualViewport?.addEventListener("resize", recalc);

  // Calculate initial sheet height based on header content
  setSheetCollapsedHeight();
  readVars();

  // Expose openSheet function globally for gallery auto-open functionality
  window.openSheet = openSheet;
  // Expose closeSheet so other modules (e.g. minicart) can close it programmatically
  window.closeSheet = closeSheet;
  // Expose updateHeight function globally for product.js
  window.updateSheetHeight = setSheetCollapsedHeight;
})();
